import { useTranslation } from '@mint-lib/i18n';
import { useMutation } from '@mint-lib/query-client';
import { useInstance } from '@mint-lib/routing-context';

const PARTIAL_SUCCESS_CODE = 207;
const CONFLICT_STATUS_CODE = 409;
export const IMPORT_VALIDATION_MUTATION_KEY = ['media-plan-import-validation'];

/** Another import into the same activity is still running. */
export class ImportAlreadyRunningError extends Error {}

interface ImportActivityResponse {
  file: Blob;
  fileName: string;
}

export const useImportActivity = () => {
  const [http] = useInstance('http', 'defaultApi');
  const { t } = useTranslation('@myn/planning_v2');

  // TODO: PDT-95430 Refactor this mutation once backend-side file storage is implemented, as the api-sdk currently lacks the flexibility to handle both blob and JSON responses for multipart form data.

  return useMutation({
    mutationKey: IMPORT_VALIDATION_MUTATION_KEY,
    mutationFn: async ({
      file,
      activityId,
    }: {
      file: File;
      activityId: string | number;
    }): Promise<ImportActivityResponse | null> => {
      const formData = new FormData();
      formData.append('file', file);

      try {
        const response = await http?.request({
          url: `api/planning-service/activities/${activityId}/import/xlsx/`,
          method: 'POST',
          data: formData,
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          // @ts-expect-error TS2339 There is issue with blob file and dataToCamelCase interceptor
          clientOptions: {
            response: {
              camelCaseBody: false,
            },
          },
          responseType: 'blob',
        });

        if (response?.status === PARTIAL_SUCCESS_CODE) {
          const disposition = response?.headers['content-disposition'];

          const fileName = disposition?.match(/filename="?([^"]+)"?/)?.[1];

          const blob = new Blob([response?.data]);

          return { file: blob, fileName };
        }

        return null;
      } catch (error) {
        if (error.response?.status === CONFLICT_STATUS_CODE) {
          throw new ImportAlreadyRunningError(
            t('Operation is running already.'),
          );
        }

        throw new Error(t('An unexpected error occurred during import.'));
      }
    },
  });
};
